import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
import { useCallback, useState } from "react";
import { Box, Button } from "@mui/material";
import { match } from "ts-pattern";
import { blobToBase64 } from "../../../util/util.js";
export function Base64FileUpload(props) {
    const [loading, setLoading] = useState(false);
    const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async (e) => {
        setLoading(true);
        try {
            const files = e.target.files !== null ? Array.from(e.target.files) : [];
            // Process each file concurrently. We can't guarantee the order in
            // which they complete, so we collect up all of the promises.
            const allFilePromises = files.map((file) => processFile(file, props.transcodeImage));
            // Wait until all of the promises have "settled".
            // Filter out any files which are rejected.
            const allFiles = (await Promise.allSettled(allFilePromises))
                .filter((filePromise) => isFulfilled(filePromise))
                .map((filePromise) => 
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            filePromise.value);
            // Hand the processed files back to the caller
            props.onDone(allFiles);
        }
        finally {
            e.target.value = "";
            setLoading(false);
        }
    }, [props]);
    // Map our domain term onto the HTML `capture` attribute. "environment" forces
    // the rear camera; false omits the attribute so the browser offers the full
    // camera-or-files chooser.
    const captureAttribute = match(props.uploadSource ?? "camera")
        .with("camera", () => "environment")
        .with("cameraOrFiles", () => false)
        .exhaustive();
    return (_jsxs(Button, { component: "label", "data-testid": "upload-button", variant: "outlined", disabled: props.disabled || loading, children: [props.icon && _jsx(Box, { sx: { mr: 1 }, children: props.icon }), props.label, _jsx("input", { "data-testid": "upload-input", accept: props.accept, disabled: props.disabled ?? false, onChange: handleChange, type: "file", multiple: props.multi, capture: captureAttribute, hidden: true })] }));
}
async function processFile(file, transcodeImageProps) {
    return new Promise((resolve, reject) => {
        // Make new FileReader
        const reader = new FileReader();
        // Success handler
        reader.onload = async () => {
            // Optionally transcode the image
            const isImage = file.type.startsWith("image/");
            let transcodedImage = undefined;
            if (isImage && transcodeImageProps !== undefined && reader.result) {
                try {
                    transcodedImage = await transcodeImage(file.type, reader.result, transcodeImageProps);
                    // eslint-disable-next-line no-empty
                }
                catch { }
            }
            // Make a fileInfo Object
            const fileInfo = {
                name: file.name,
                type: file.type,
                size: file.size,
                base64: reader.result ?? "",
                file: file,
                transcodedImage,
            };
            resolve(fileInfo);
        };
        // Failure handler
        reader.onerror = () => {
            reject("Failed to read file");
        };
        // Having attached the handlers, trigger the processing
        reader.readAsDataURL(file);
    });
}
async function transcodeImage(inputMimeType, inputContent, config) {
    return new Promise((resolve, reject) => {
        const mimeType = config.mimeType ?? inputMimeType;
        const img = new Image();
        // Success handler
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const [width, height] = calculateSize(img.width, img.height, config.maxWidth, config.maxHeight);
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            if (context !== null) {
                context.drawImage(img, 0, 0, width, height);
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                canvas.toBlob(async (blob) => {
                    if (blob === null) {
                        reject("Failed to transcode image");
                    }
                    else {
                        try {
                            const base64 = await blobToBase64(blob);
                            resolve({ width, height, blob, base64 });
                        }
                        catch {
                            reject("Failed to transcode image");
                        }
                    }
                }, mimeType);
            }
        };
        // Failure handler
        img.onerror = () => {
            reject("Failed to transcode image");
        };
        // Having attached the handlers, trigger the image loading
        img.src = inputContent;
    });
}
// This can be used to filter out rejected Promises
function isFulfilled(val) {
    return val.status === "fulfilled";
}
// This clamps the width and height to the maxWidth and maxHeight
// while maintaining the aspect ratio.
function calculateSize(width, height, maxWidth, maxHeight) {
    // If the image is already smaller than the maximum size, return it as is
    if (width < maxWidth && height < maxHeight)
        return [width, height];
    const widthRatio = width / maxWidth;
    const heightRatio = height / maxHeight;
    const ratio = Math.max(widthRatio, heightRatio);
    return [Math.round(width / ratio), Math.round(height / ratio)];
}
//# sourceMappingURL=Base64FileUpload.js.map